/**
 * Aggregate Reserve Screen
 *
 * Combines loss, dilution, and yield/fee reserves into the fungible
 * structural test that actually applies (per Loss Reserve guidance
 * A119–A129 and Yield Reserve guidance A99–A103; mirrors Key Perf Ratios
 * sheet col S/V/W with yield folded into the dynamic side).
 *
 *   combined_dynamic = loss_reserve_dynamic + dilution_reserve_dynamic + yield_fee_reserve
 *   combined_floor   = loss_reserve_floor   + dilution_reserve_floor    (no yield floor)
 *   aggregate        = MAX(combined_dynamic, combined_floor)
 *
 * Requires receivables_loss, receivables_dilution, and receivables_yield to be calculated first.
 */
import { useState } from "react";
import { useDispatch } from "react-redux";
// -------------------------------------------------------------
import { calculateAggregateReserve, getAggregateReserveData } from "Redux/data/actions";
import { useAnalysis, useScenarioFlag } from "Redux/data/hooks";
import DataTable from "Components/DataTable";
import SkeletonTable from "Components/Skeleton";

const headers = [
  { text: "Reference Date", key: "Reference Date", datatype: "date" },
  { text: "Loss Reserve Dynamic", key: "loss_reserve_dynamic", datatype: "float", display: "percentage" },
  { text: "Dilution Reserve Dynamic", key: "dilution_reserve_dynamic", datatype: "float", display: "percentage" },
  { text: "Yield & Fee Reserve", key: "yield_fee_reserve", datatype: "float", display: "percentage" },
  { text: "Combined Dynamic", key: "combined_dynamic", datatype: "float", display: "percentage" },
  { text: "Loss Reserve Floor", key: "loss_reserve_floor", datatype: "float", display: "percentage" },
  { text: "Dilution Reserve Floor", key: "dilution_reserve_floor", datatype: "float", display: "percentage" },
  { text: "Combined Floor", key: "combined_floor", datatype: "float", display: "percentage" },
  { text: "Aggregate Reserve", key: "aggregate_reserve", datatype: "float", display: "percentage" },
];

const AggregateReserve = () => {
  const dispatch = useDispatch();
  const [calculating, setCalculating] = useState(false);

  const { data: aggregateReserve, has: hasAggregateReserve } = useAnalysis("aggregateReserve", getAggregateReserveData);
  const hasLoss = useScenarioFlag("hasLoss");
  const hasDilution = useScenarioFlag("hasDilution");
  const hasYield = useScenarioFlag("hasYield");

  if (!hasLoss || !hasDilution || !hasYield)
    return (
      <div className="notification is-warning is-light">
        Calculate Loss Reserve, Dilution Reserve, and Yield &amp; Fee Reserve first — all three are required inputs to the aggregate fungible test.
      </div>
    );

  return (
    <div>
      <div className="box" style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <button
          className={`button is-small ${calculating ? "is-loading" : ""}`}
          onClick={() => {
            setCalculating(true);
            dispatch(calculateAggregateReserve(() => setCalculating(false)));
          }}
        >
          {hasAggregateReserve ? "Recalculate" : "Calculate"} Aggregate Reserve
        </button>
      </div>
      {hasAggregateReserve && !aggregateReserve && <SkeletonTable />}
      {aggregateReserve && (
        <DataTable
          data={aggregateReserve.data}
          headers={headers}
          reference="Reference Date"
        />
      )}
    </div>
  );
};

export default AggregateReserve;
